import { Fragment, useState, useMemo } from 'react';
import { NavLink, useParams, Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Table, ChevronDown, ChevronRight } from 'lucide-react';
import { api, fmt, type System, type Carrier } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ExcelExportButton from '../components/ExcelExportButton';
import { CarrierMappingsBody } from './MappingsCarrier';
import { cleanPolicyId, isHouseForSystem } from '../lib/format';
import type { SheetSpec } from '../lib/xlsx-export';

/**
 * Single tabbed page absorbing the legacy Carriers / Mappings / Setup pages.
 * URL: /:sys/carriers/:carrier (where :carrier is a carrier name OR "setup").
 */
export default function CarriersPage() {
  const { sys: sysSlug, carrier } = useParams<{ sys: string; carrier?: string }>();
  const sys = sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null;
  const activeTab = carrier ? decodeURIComponent(carrier) : null;

  const carriersQuery = useQuery({
    queryKey: ['carriers', sys],
    queryFn: () => api.listCarriers(sys as System),
    enabled: !!sys,
  });

  const carriers = carriersQuery.data?.carriers ?? [];

  const sortedCarriers = useMemo(
    () => [...carriers].sort((a, b) => a.carrier.localeCompare(b.carrier)),
    [carriers],
  );
  const defaultTab = sortedCarriers[0]?.carrier ?? '';

  if (!sys) return <div className="alert alert-warning"><p>Pick a system.</p></div>;
  if (carriersQuery.isLoading) return <LoadingSpinner label="Loading carriers…" />;
  if (sortedCarriers.length === 0) {
    return (
      <div className="space-y-4 animate-fadeIn">
        <CarriersHeader sys={sys} />
        <div className="card text-center py-12">
          <p style={{ color: 'var(--color-text-muted)' }}>
            No carriers yet. Drop a Messer commission CSV in{' '}
            <Link to={`/${sysSlug}/imports`} style={{ color: 'var(--color-primary)' }}>
              Imports
            </Link>{' '}
            and they auto-discover.
          </p>
        </div>
      </div>
    );
  }

  // No tab in URL → redirect to default
  if (!activeTab) {
    return <Navigate to={`/${sysSlug}/carriers/${encodeURIComponent(defaultTab)}`} replace />;
  }

  const activeCarrier = sortedCarriers.find((c) => c.carrier === activeTab) ?? null;

  return (
    <div className="space-y-4 animate-fadeIn">
      <CarriersHeader sys={sys} />

      {/* Tab strip */}
      <div
        className="flex items-center gap-1 overflow-x-auto"
        style={{ borderBottom: '1px solid var(--color-border)' }}
      >
        {sortedCarriers.map((c) => (
          <CarrierTab
            key={c.SK}
            sysSlug={sysSlug!}
            carrier={c.carrier}
            label={c.display_name || c.carrier}
            active={activeTab === c.carrier}
          />
        ))}
      </div>

      {/* Tab body */}
      {activeCarrier ? (
        <CarrierTabBody sys={sys} carrier={activeCarrier} />
      ) : (
        <div className="card text-center py-8">
          <p style={{ color: 'var(--color-text-muted)' }}>
            Carrier "{activeTab}" not found.{' '}
            <Link to={`/${sysSlug}/carriers/${encodeURIComponent(defaultTab)}`} style={{ color: 'var(--color-primary)' }}>
              Back to default tab →
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}

function CarriersHeader({ sys }: { sys: System }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
        <Table size={20} className="inline mr-2" style={{ color: 'var(--color-primary)' }} />
        Carriers — {sys}
      </h2>
      <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
        Per-carrier mappings, row data, and config. Pick a carrier tab.
      </p>
    </div>
  );
}

function CarrierTab({
  sysSlug,
  carrier,
  label,
  active,
}: {
  sysSlug: string;
  carrier: string;
  label: React.ReactNode;
  active: boolean;
}) {
  return (
    <NavLink
      to={`/${sysSlug}/carriers/${encodeURIComponent(carrier)}`}
      className="text-sm px-3 py-2 -mb-px"
      style={{
        borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
        fontWeight: active ? 600 : 400,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </NavLink>
  );
}

// ===== Carrier tab body =====

function CarrierTabBody({ sys, carrier }: { sys: System; carrier: Carrier }) {
  const [showRows, setShowRows] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <CarrierHeader carrier={carrier} />
        <ExcelExportButton
          filename={`carrier-${carrier.carrier.replace(/\s+/g, '_')}-${sys.toLowerCase()}.xlsx`}
          sheets={async (): Promise<SheetSpec[]> => {
            // Pull rows + mappings + agents together. Build 3 sheets:
            // per-agent totals, mappings, row-by-row.
            const [rowsResp, mapResp, agentsResp] = await Promise.all([
              api.rowsByCarrier(sys, carrier.carrier),
              api.listMappings(sys, carrier.carrier),
              api.listAgents(),
            ]);
            const rows = rowsResp.rows ?? [];
            const housePayoutFalse = new Set(
              (agentsResp.agents ?? []).filter((a) => isHouseForSystem(a, sys)).map((a) => a.agent_id),
            );
            // Per-agent
            const byAgent = new Map<string, { name: string; rows: number; net: number }>();
            for (const r of rows) {
              const isHouse =
                housePayoutFalse.has(r.agent_id) ||
                r.agent_id === 'unknown' ||
                r.agent_id === 'unpaid';
              const key = isHouse ? 'house' : r.agent_id;
              const name = isHouse ? 'House' : r.agent_name;
              if (!byAgent.has(key)) byAgent.set(key, { name, rows: 0, net: 0 });
              const a = byAgent.get(key)!;
              a.rows++;
              a.net += r.net_amount || 0;
            }
            const agentSheet: SheetSpec = {
              name: 'Per agent',
              rows: [
                ['Agent', 'Rows', 'Net'],
                ...[...byAgent.values()]
                  .sort((a, b) => b.net - a.net)
                  .map((a) => [a.name, a.rows, a.net] as (string | number | null)[]),
              ],
            };
            const mapSheet: SheetSpec = {
              name: 'Mappings',
              rows: [
                ['Writing ID', 'Agent', 'Rows', 'Source', 'Direct pay', 'Non-paid'],
                ...((mapResp.mappings ?? []).map((m) => [
                  m.writing_id,
                  m.agent_name,
                  m.row_count,
                  m.source,
                  m.direct_pay_by_carrier ? 'yes' : 'no',
                  m.non_paid ? 'yes' : 'no',
                ]) as (string | number | null)[][]),
              ],
            };
            const rowsSheet: SheetSpec = {
              name: 'Rows',
              rows: [
                ['Policy', 'Policyholder', 'Writing ID', 'Agent', 'Raw', 'OVR', 'Agent net', 'Paid-to date'],
                ...rows.map((r) => [
                  cleanPolicyId(r.policy_id, r.writing_agent_id),
                  r.policyholder_name ?? '',
                  r.writing_agent_id ?? '',
                  r.agent_name ?? '',
                  r.raw_amount ?? null,
                  r.override_amount ?? null,
                  r.net_amount ?? null,
                  r.paid_to_date ?? r.transaction_date ?? '',
                ] as (string | number | null)[]),
              ],
            };
            return [agentSheet, mapSheet, rowsSheet];
          }}
        />
      </div>
      <CarrierAgentSummary sys={sys} carrierName={carrier.carrier} />
      <CarrierMappingsBody sys={sys} carrier={carrier.carrier} />
      <div>
        <button
          onClick={() => setShowRows((v) => !v)}
          className="btn btn-secondary text-sm flex items-center gap-2"
        >
          {showRows ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {showRows ? 'Hide row-by-row detail' : 'View row-by-row detail'}
        </button>
        {showRows && <CarrierRowsTable sys={sys} carrierName={carrier.carrier} />}
      </div>
    </div>
  );
}

function CarrierHeader({ carrier }: { carrier: Carrier }) {
  const overrideText =
    carrier.override_spec.kind === 'use_column'
      ? `column "${carrier.override_spec.column}"`
      : 'passthrough';
  return (
    <div className="card flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
          {carrier.display_name || carrier.carrier}
        </h3>
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Attribution: <span className="font-mono">{carrier.attribution_method.replace(/_/g, ' ')}</span> · Override:{' '}
          <span className="font-mono">{overrideText}</span>
          {carrier.parser_id && (
            <>
              {' '}· Parser: <span className="font-mono">{carrier.parser_id}</span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function CarrierAgentSummary({ sys, carrierName }: { sys: System; carrierName: string }) {
  const [houseExpanded, setHouseExpanded] = useState(false);

  const rowsQuery = useQuery({
    queryKey: ['carrier-rows', sys, carrierName],
    queryFn: () => api.rowsByCarrier(sys, carrierName),
  });
  const agentsQuery = useQuery({
    queryKey: ['agents-all'],
    queryFn: () => api.listAgents(),
  });

  if (rowsQuery.isLoading) return <LoadingSpinner label="Loading rows…" />;

  const rows = rowsQuery.data?.rows ?? [];
  const housePayoutFalse = new Set<string>();
  for (const a of agentsQuery.data?.agents ?? []) {
    if (isHouseForSystem(a, sys)) housePayoutFalse.add(a.agent_id);
  }

  const totals = rows.reduce(
    (acc, r) => {
      acc.raw += r.raw_amount || 0;
      acc.net += r.net_amount || 0;
      acc.override += r.override_amount || 0;
      return acc;
    },
    { raw: 0, net: 0, override: 0 },
  );

  const byAgent = new Map<string, { agent_name: string; rows: number; net: number }>();
  const houseMembers: { agent_id: string; agent_name: string; rows: number; net: number }[] = [];
  const houseAgg = { agent_name: 'House', rows: 0, net: 0 };
  let hasHouse = false;
  // Unmapped writing IDs (agent_id 'unknown'/'unpaid') roll into the House
  // bucket — matches the backend handlePivot/handlePaySummary behavior.
  const isHouseRow = (r: { agent_id: string }) =>
    housePayoutFalse.has(r.agent_id) ||
    r.agent_id === 'unknown' ||
    r.agent_id === 'unpaid';
  for (const r of rows) {
    if (isHouseRow(r)) {
      hasHouse = true;
      houseAgg.rows++;
      houseAgg.net += r.net_amount || 0;
      const memberKey = r.agent_id;
      const memberName =
        memberKey === 'unknown' || memberKey === 'unpaid'
          ? '(unmapped)'
          : r.agent_name;
      const m = houseMembers.find((x) => x.agent_id === memberKey);
      if (m) {
        m.rows++;
        m.net += r.net_amount || 0;
      } else {
        houseMembers.push({
          agent_id: memberKey,
          agent_name: memberName,
          rows: 1,
          net: r.net_amount || 0,
        });
      }
      continue;
    }
    if (!byAgent.has(r.agent_id)) byAgent.set(r.agent_id, { agent_name: r.agent_name, rows: 0, net: 0 });
    const a = byAgent.get(r.agent_id)!;
    a.rows++;
    a.net += r.net_amount || 0;
  }
  if (hasHouse) byAgent.set('house', houseAgg);

  return (
    <div className="card p-0 overflow-hidden">
      <div
        className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        style={{ borderBottom: '1px solid var(--color-border)' }}
      >
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          Per-agent totals
        </h3>
        <div className="text-xs flex gap-4" style={{ color: 'var(--color-text-muted)' }}>
          <span>{rows.length.toLocaleString()} rows</span>
          <span>raw {fmt.money(totals.raw)}</span>
          <span>override {fmt.money(totals.override)}</span>
          <span style={{ color: 'var(--color-text)' }}>net {fmt.money(totals.net)}</span>
        </div>
      </div>
      {byAgent.size === 0 ? (
        <p className="p-4 text-sm text-center" style={{ color: 'var(--color-text-muted)' }}>
          No rows for this carrier yet.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Agent</th>
              <th className="text-right">Rows</th>
              <th className="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {[...byAgent.entries()]
              .sort((a, b) => b[1].net - a[1].net)
              .map(([id, a]) => {
                const isHouse = id === 'house';
                if (!isHouse) {
                  return (
                    <tr key={id}>
                      <td>{a.agent_name}</td>
                      <td className="text-right font-mono">{a.rows}</td>
                      <td className="text-right font-mono font-semibold">{fmt.money(a.net)}</td>
                    </tr>
                  );
                }
                return (
                  <Fragment key={id}>
                    <tr
                      onClick={() => setHouseExpanded((v) => !v)}
                      style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.02)' }}
                    >
                      <td>
                        <ChevronRight
                          size={12}
                          style={{
                            display: 'inline-block',
                            verticalAlign: -1,
                            transform: houseExpanded ? 'rotate(90deg)' : 'none',
                            transition: 'transform 0.15s',
                            marginRight: 6,
                            color: 'var(--color-text-muted)',
                          }}
                        />
                        House{' '}
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          ({houseMembers.length} agents)
                        </span>
                      </td>
                      <td className="text-right font-mono">{a.rows}</td>
                      <td className="text-right font-mono font-semibold">{fmt.money(a.net)}</td>
                    </tr>
                    {houseExpanded &&
                      houseMembers
                        .sort((x, y) => y.net - x.net)
                        .map((m) => (
                          <tr key={`house-${m.agent_id}`} style={{ opacity: 0.85 }}>
                            <td className="text-sm" style={{ paddingLeft: 36, color: 'var(--color-text-muted)' }}>
                              {m.agent_name}
                            </td>
                            <td className="text-right font-mono text-xs" style={{ color: 'var(--color-text-muted)' }}>
                              {m.rows}
                            </td>
                            <td className="text-right font-mono text-xs" style={{ color: 'var(--color-text-muted)' }}>
                              {fmt.money(m.net)}
                            </td>
                          </tr>
                        ))}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CarrierRowsTable({ sys, carrierName }: { sys: System; carrierName: string }) {
  const rowsQuery = useQuery({
    queryKey: ['carrier-rows', sys, carrierName],
    queryFn: () => api.rowsByCarrier(sys, carrierName),
  });
  if (rowsQuery.isLoading) return <LoadingSpinner label="Loading rows…" />;
  const rows = rowsQuery.data?.rows ?? [];
  return (
    <div className="card overflow-x-auto p-0 mt-2">
      <table className="table">
        <thead>
          <tr>
            <th>Policy</th>
            <th>Policyholder</th>
            <th>Writing ID</th>
            <th>Agent</th>
            <th className="text-right">Raw</th>
            <th className="text-right">Override</th>
            <th className="text-right">Net</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
                No rows for this carrier yet.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={`${r.batch_id}-${r.row_idx}`}>
                <td className="font-mono text-xs">{r.policy_id}</td>
                <td>{r.policyholder_name}</td>
                <td className="font-mono text-xs">{r.writing_agent_id}</td>
                <td>{r.agent_name}</td>
                <td className="text-right font-mono">{fmt.money(r.raw_amount)}</td>
                <td
                  className="text-right font-mono text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                  title={r.override_formula}
                >
                  {fmt.money(r.override_amount)}
                </td>
                <td className="text-right font-mono font-semibold">{fmt.money(r.net_amount)}</td>
                <td className="text-xs">{fmt.dateOnly(r.transaction_date)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

